import React, { useMemo } from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  confidenceChip: {
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'rgba(255,255,255,0.04)',
    color: '#cfd6ff',
    fontSize: 11,
    fontWeight: 800,
    padding: '4px 10px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  },
  section: {
    marginTop: 12,
  },
  sectionTitle: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  row: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '10px 12px',
    marginBottom: 8,
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'rgba(77, 121, 255, 0.42)',
      background: 'rgba(16, 24, 50, 0.92)',
    },
  },
  rowTop: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
  },
  narrative: {
    color: '#e5e7ff',
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.3,
  },
  usdValue: {
    color: '#eef1ff',
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
  },
  meta: {
    color: '#8d93c9',
    fontSize: 11,
    lineHeight: 1.35,
    marginTop: 4,
  },
  inflowAccent: {
    color: '#76ffb8',
  },
  outflowAccent: {
    color: '#ff8c8c',
  },
  empty: {
    color: '#8d93c9',
    fontSize: 12,
    lineHeight: 1.45,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
}));

function formatUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '$0';
  const abs = Math.abs(n);
  const fmt = (v, digits = 1) => {
    const rounded = v.toFixed(digits);
    const cleaned = rounded.replace(/\.0$/, '');
    return cleaned;
  };
  if (abs >= 1e9) return `$${fmt(abs / 1e9)}B`;
  if (abs >= 1e6) return `$${fmt(abs / 1e6)}M`;
  if (abs >= 1e3) return `$${fmt(abs / 1e3)}k`;
  return `$${Math.round(abs).toLocaleString('en-US')}`;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeConfidence(confidence) {
  const c = confidence && typeof confidence === 'object' ? confidence : {};
  const level = String(c.level || '').trim() || 'Low';
  const walletCount = Number.isFinite(Number(c.wallet_count)) ? Number(c.wallet_count) : 0;
  const txCount = Number.isFinite(Number(c.tx_count)) ? Number(c.tx_count) : 0;
  return { level, walletCount, txCount };
}

export default function NarrativeFlowPanel({ address, metadata, flow, loading, error, onNarrativeClick, onRotationClick }) {
  const classes = useStyles();

  const resolvedFlow = flow && typeof flow === 'object' ? flow : null;
  const inflow = useMemo(() => asArray(resolvedFlow && resolvedFlow.inflow_by_narrative).slice(0, 3), [resolvedFlow]);
  const outflow = useMemo(() => asArray(resolvedFlow && resolvedFlow.outflow_by_narrative).slice(0, 3), [resolvedFlow]);
  const rotations = useMemo(() => asArray(resolvedFlow && resolvedFlow.rotation_events).slice(0, 5), [resolvedFlow]);
  const confidence = useMemo(() => normalizeConfidence(resolvedFlow && resolvedFlow.confidence), [resolvedFlow]);

  const handleNarrativeClick = (row, section) => {
    if (typeof onNarrativeClick === 'function') {
      onNarrativeClick(row, section);
      return;
    }
    try {
      // placeholder handler for future routing
      // eslint-disable-next-line no-console
      console.debug('[narrative-flow] narrative click', { address, section, row, symbol: metadata && metadata.symbol });
    } catch (e) {
    }
  };

  const handleRotationClick = (row) => {
    if (typeof onRotationClick === 'function') {
      onRotationClick(row);
      return;
    }
    try {
      // placeholder handler for future routing
      // eslint-disable-next-line no-console
      console.debug('[narrative-flow] rotation click', { address, row, symbol: metadata && metadata.symbol });
    } catch (e) {
    }
  };

  return (
    <Box className={classes.panel}>
      <Box className={classes.headerRow}>
        <Typography className={classes.title}>Narrative Flow (24h)</Typography>
        <Box className={classes.confidenceChip}>
          Confidence {confidence.level} ({confidence.walletCount} wallets / {confidence.txCount} tx)
        </Box>
      </Box>
      <Divider className={classes.divider} />

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Inflow by Narrative</Typography>
        {inflow.length ? inflow.map((row) => (
          <Box
            key={`in-${row.narrative || 'narrative'}-${row.usd_value}`}
            className={classes.row}
            onClick={() => handleNarrativeClick(row, 'inflow')}
          >
            <Box className={classes.rowTop}>
              <Typography className={classes.narrative}>{row.narrative || 'Unclassified'}</Typography>
              <Typography className={`${classes.usdValue} ${classes.inflowAccent}`}>+{formatUsd(row.usd_value)}</Typography>
            </Box>
            <Typography className={classes.meta}>({Number(row.wallet_count || 0)} wallets)</Typography>
          </Box>
        )) : <Typography className={classes.empty}>{loading ? 'Loading inflow…' : 'No significant inflow signals in the last 24h.'}</Typography>}
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Outflow by Narrative</Typography>
        {outflow.length ? outflow.map((row) => (
          <Box
            key={`out-${row.narrative || 'narrative'}-${row.usd_value}`}
            className={classes.row}
            onClick={() => handleNarrativeClick(row, 'outflow')}
          >
            <Box className={classes.rowTop}>
              <Typography className={classes.narrative}>{row.narrative || 'Unclassified'}</Typography>
              <Typography className={`${classes.usdValue} ${classes.outflowAccent}`}>-{formatUsd(row.usd_value)}</Typography>
            </Box>
            <Typography className={classes.meta}>({Number(row.wallet_count || 0)} wallets)</Typography>
          </Box>
        )) : <Typography className={classes.empty}>{loading ? 'Loading outflow…' : 'No significant outflow signals in the last 24h.'}</Typography>}
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Rotation Events</Typography>
        {rotations.length ? rotations.map((row, idx) => {
          const src = row.source_token_symbol || row.source_narrative || 'Token';
          const dst = row.destination_token_symbol || row.destination_narrative || 'Token';
          const label = `${src} → ${dst}`;
          return (
            <Box
              key={`rot-${row.source_token_address || idx}-${row.destination_token_address || idx}`}
              className={classes.row}
              onClick={() => handleRotationClick(row)}
            >
              <Box className={classes.rowTop}>
                <Typography className={classes.narrative}>{label}</Typography>
                <Typography className={classes.usdValue}>{formatUsd(row.usd_value)}</Typography>
              </Box>
              <Typography className={classes.meta}>({Number(row.wallet_count || 0)} wallets)</Typography>
            </Box>
          );
        }) : <Typography className={classes.empty}>{loading ? 'Loading rotations…' : 'No major rotation events above the minimum threshold.'}</Typography>}
      </Box>

      {error ? <Typography className={classes.error}>{error}</Typography> : null}
    </Box>
  );
}
